import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "./icons";

import { cn } from "utils/cn";
import { useFormFieldControl } from "./FormField";
import { useStableId } from "./useStableId";

/** Focus ring of the small toggle controls (Checkbox, Switch, Radio). */
export const toggleFocusClasses =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const boxClasses = cn(
  "group/checkbox inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-input bg-surface text-primary-foreground shadow-xs",
  "transition-colors duration-fast ease-standard",
  toggleFocusClasses,
  "disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-danger",
  "data-[state=checked]:border-primary data-[state=checked]:bg-primary",
  "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary"
);

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /** Text next to the box; the whole row then toggles the checkbox. */
  label?: React.ReactNode;
  description?: React.ReactNode;
}

/** Pass `checked="indeterminate"` for a partly selected group (shows a dash). */
export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ label, description, className, ...rest }, ref) => {
  // Without its own label the box takes the surrounding FormField's id and aria wiring.
  const fieldProps = useFormFieldControl(rest);
  const id = useStableId("checkbox", rest.id);

  const indicator = (
    <CheckboxPrimitive.Indicator className="flex items-center justify-center">
      <Check
        aria-hidden="true"
        weight="bold"
        className="h-3 w-3 group-data-[state=indeterminate]/checkbox:hidden"
      />
      <Minus
        aria-hidden="true"
        weight="bold"
        className="hidden h-3 w-3 group-data-[state=indeterminate]/checkbox:block"
      />
    </CheckboxPrimitive.Indicator>
  );

  if (!label) {
    return (
      <CheckboxPrimitive.Root
        ref={ref}
        data-ui="checkbox"
        className={cn(boxClasses, className)}
        {...fieldProps}
      >
        {indicator}
      </CheckboxPrimitive.Root>
    );
  }

  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  return (
    <label
      data-ui="checkbox"
      htmlFor={id}
      className={cn(
        "flex items-start gap-3",
        rest.disabled ? "cursor-not-allowed" : "cursor-pointer",
        className
      )}
    >
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(boxClasses, "mt-0.5")}
        {...rest}
        id={id}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : rest["aria-describedby"]}
      >
        {indicator}
      </CheckboxPrimitive.Root>
      <span className={cn("flex min-w-0 flex-col gap-0.5", rest.disabled && "opacity-60")}>
        <span id={labelId} className="text-sm font-medium leading-5 text-foreground">
          {label}
        </span>
        {description && (
          <span id={descriptionId} className="text-sm leading-5 text-muted-foreground">
            {description}
          </span>
        )}
      </span>
    </label>
  );
});
Checkbox.displayName = "Checkbox";
